/**
 * main.tsx — nsentry SPA entry point
 *
 * Purpose:    React SPA root render for sentry.nself.org.
 *             Sets html[lang] and html[dir] for RTL locale support.
 *             Initialises Sentry error reporting + OTel tracing via @nself/observability.
 * Inputs:     #root DOM element; navigator.language for locale detection
 * Outputs:    React app mounted with React Router; Sentry + OTel active when DSN is set.
 * Constraints: No SSR; client-only routing
 *   - RTL is CSS-driven (dir attribute + Tailwind rtl: variants), not conditional rendering
 *   - Sentry DSN from VITE_SENTRY_DSN (set from SENTRY_DSN_NSENTRY in vault.env)
 *   - PII scrubbing via scrubEvent strips email, userId, and digit tokens
 * SOT:        T-P3-E6-W1-S3-T01 — RTL layout support (Vite SPA surfaces)
 * SPORT:      T-P3-E6-W2-S1-T01 — observability wiring
 *
 * Bundle budget (P6-E11-W2-S4-T30): `import * as Sentry from '@sentry/react'` pulls in
 * @sentry/react's v8 core, which bundles OTel-based tracing internally rather than as a
 * separable optional integration — this is nsentry dogfooding its own product's own SDK,
 * not incidental bloat. Measured gzip size is ~319 KB against the shared marketing-tier
 * 250 KB budget; .size-limit.json's nsentry entry was raised to 340 KB (justified here,
 * not silently) rather than dropping error tracking from nsentry's own marketing page.
 */
import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import { ThemeProvider } from 'next-themes'
import { scrubEvent, initObservability } from '@nself/observability'
import { NselfI18nProvider } from '@nself/i18n'
import { App } from './App'
import './styles/globals.css'

/** RTL locales — dir="rtl" is applied for these base locale codes */
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur'])

// Locale detection: browser Accept-Language primary subtag; fall back to 'en'.
const detectedLocale = (navigator.language ?? 'en').split('-')[0] || 'en'

// Apply lang + dir to the HTML root immediately (before React hydrates)
document.documentElement.lang = detectedLocale
document.documentElement.dir = RTL_LOCALES.has(detectedLocale) ? 'rtl' : 'ltr'

// ─── Observability (Sentry + OTel) ───────────────────────────────────────────
// DSN: VITE_SENTRY_DSN in .env.local (set from SENTRY_DSN_NSENTRY in vault.env).
initObservability({
  sentry: {
    sdk: Sentry,
    dsn: import.meta.env['VITE_SENTRY_DSN'] ?? '',
    environment: import.meta.env['VITE_ENV'] ?? 'production',
    release: import.meta.env['VITE_APP_VERSION'] ?? '0.0.0',
    appKind: 'web',
    tracesSampleRate: 0.1,
  },
  otel: {
    serviceName: 'nsentry-web',
    endpoint: import.meta.env['VITE_OTEL_ENDPOINT'],
  },
})

// PII scrubber: register as an event processor so all events are scrubbed
// before upload (strips email addresses, UUIDs, and digit-token strings).
if (import.meta.env['VITE_SENTRY_DSN']) {
  Sentry.addEventProcessor((event) => scrubEvent(event as Record<string, unknown>) as typeof event)
}

// ─── Theme ───────────────────────────────────────────────────────────────────
// Follows the canonical nself.org theme contract, not a system-following default.
//
// nself.org boots with an inline script whose fallback is `: true` — i.e. it is
// DARK-FIRST, with light strictly opt-in. Using defaultTheme="system" here would
// have given a visitor whose OS prefers light a light subdomain while nself.org
// stayed dark, which is exactly the mismatch this is meant to remove.
//
// The inline script in index.html runs first and reads, in order:
//   cookie `nself_theme` (set on .nself.org) -> localStorage -> dark
// The cookie is what actually carries a theme choice ACROSS subdomains;
// localStorage is per-origin and never travels from nself.org to here. That
// script copies the cookie value into localStorage so next-themes, which reads
// the same `nself_theme` key, agrees with it instead of fighting it.
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <NselfI18nProvider locale={detectedLocale as Parameters<typeof NselfI18nProvider>[0]['locale']}>
      <ThemeProvider
        attribute="class"
        defaultTheme="dark"
        storageKey="nself_theme"
        disableTransitionOnChange
      >
        <App />
      </ThemeProvider>
    </NselfI18nProvider>
  </React.StrictMode>,
)
